import { useToast } from '@chakra-ui/react';
import { clearStorage } from '@getjust/commons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'next-i18next';
import { useCallback, useRef } from 'react';

import type { ReshapedUser } from '$src/types';
import type { CheckoutV1 } from '@getjust/api-schema';

import { useCart, useUserInfo } from '$hooks/queries';
import { useFormMessageAtom } from '$hooks/state/useFormMessageAtom';
import { usePaymentStatusAtom } from '$hooks/state/usePaymentStatusAtom';
import {
  CART_QUERY_KEY,
  MAX_FAILURE_COUNT,
  ORDER_REQUEST_QUERY_KEY,
  USER_INFO_QUERY_KEY,
} from '$src/constants';
import { useAuth } from '$src/hooks/auth';
import { AnalyticsEventName, useTrackAnalytics } from '$src/hooks/client/useTrackAnalytics';
import { useCMSInterceptors } from '$src/hooks/interceptors';
import { useCleanupCart } from '$src/hooks/mutations/useCleanupCart';
import { useErrorAtom } from '$src/hooks/state/useErrorAtom';
import { usePaidLoadingAtom } from '$src/hooks/state/useLoadingAtom';
import { usePhoneVerificationAtom } from '$src/hooks/state/usePhoneVerificationAtom';
import { useShowLostConnectionAtom } from '$src/hooks/state/useShowLostConnectionAtom';
import { justAPI } from '$src/http';
import { posthog } from '$src/utils';
import { clearCachedCart } from '$src/utils/cartCache';
import { clearEmailCheck } from '$src/utils/emailCheckCache';
import { sleep } from '$src/utils/sleep';

export const getPaymentMethodForBackend = (str: string | null | undefined) => {
  switch (str) {
    case 'google_pay':
      return 'google-pay';
    case 'apple_pay':
      return 'apple-pay';
    default:
      return 'card';
  }
};

export const useOrderStatus = () => {
  const { formMessage, onFormMessageChange } = useFormMessageAtom();
  const { openConnectionAlert } = useShowLostConnectionAtom();
  const { paymentStatus } = usePaymentStatusAtom();
  const { t, i18n } = useTranslation('common');
  const toast = useToast();
  const { data: cart } = useCart();
  // Note: We use queryClient.getQueryData for fresh user info instead of useUserInfo hook
  // to ensure we have the latest verifiedPhone status when checking after order completion
  useUserInfo(); // Keep the hook to ensure user info is fetched
  const { authState } = useAuth() ?? {};
  const { track } = useTrackAnalytics();
  const { mutateAsync: cleanUpCart } = useCleanupCart();
  const interceptors = useCMSInterceptors();
  const { onErrorChange } = useErrorAtom();
  const { onPaidLoadingChange } = usePaidLoadingAtom();
  const { openPhoneVerification } = usePhoneVerificationAtom();
  const queryClient = useQueryClient();
  const timerNumber = 10; // in seconds

  // Track if we've already processed this order to prevent duplicate prompts
  const processedCartIdRef = useRef<string | null>(null);

  const handleStartTimer = () => {
    const timerId = setTimeout(() => {
      track(AnalyticsEventName.STILL_NOT_REDIRECTED);
    }, timerNumber * 1000);
    return () => clearTimeout(timerId);
  };

  // Create the redirect handler
  const createRedirectHandler = useCallback(
    (data: CheckoutV1.OrdersGetStatusResponseBody & { outcome: 'ready' }) => {
      return async () => {
        await sleep(100);
        if (formMessage?.shopId) {
          clearStorage('localStorage', `just_session_id_${formMessage.shopId}`);
          clearCachedCart(formMessage.shopId);
          clearEmailCheck(formMessage.shopId);
        }
        await interceptors.redirectToOrderPage({ cart, order: data, sourceUrl: formMessage?.origin });
      };
    },
    [cart, formMessage, interceptors],
  );

  return useQuery({
    queryKey: [ORDER_REQUEST_QUERY_KEY, paymentStatus?.cartId],
    queryFn: async () => {
      try {
        const { data } = await justAPI.get<CheckoutV1.OrdersGetStatusResponseBody>(
          `/v1/status/${paymentStatus?.cartId}`,
        );

        if (
          data?.outcome === 'ready' &&
          data?.analytics?.orderPaymentStatus === 'authorized' &&
          paymentStatus?.opened
        ) {
          track(AnalyticsEventName.THREE_DS_FRAME_VALIDATED);
        }
        if (data?.outcome === 'ready') {
          track(AnalyticsEventName.ORDER_CREATION_EXECUTED, {
            output: 'Order created',
            paymentMethod: data.analytics.paymentMethod,
          });
          try {
            track(AnalyticsEventName.ORDER_CREATED, {
              CMSOrder: data.analytics.orderId,
              productsIds: data.analytics.products.map((product) => product?.productId) as
                | string[]
                | undefined,
              totalAmount: cart?.totalAmount,
              currency: cart?.currency,
              product: cart?.lineItems?.length,
              deliveryCountry: cart?.shipping.address?.country,
              paymentMethod: getPaymentMethodForBackend(data.analytics?.paymentMethod),
            });
            await cleanUpCart();
          } catch (error) {
            console.error('Failed to clean up cart', { error });
          } finally {
            handleStartTimer();
          }

          // Create the redirect handler
          const redirectHandler = createRedirectHandler(data);

          // Fetch fresh user info to get accurate verifiedPhone status
          // This ensures we don't show the verification prompt to users who already verified
          // We invalidate first, then use refetchQueries to actually fetch fresh data and wait for it
          await queryClient.invalidateQueries({ queryKey: [USER_INFO_QUERY_KEY] });
          await queryClient.refetchQueries({ queryKey: [USER_INFO_QUERY_KEY] });
          const freshUserInfo = queryClient.getQueryData<ReshapedUser>([USER_INFO_QUERY_KEY]);

          // Check if we should prompt for phone verification with fresh data
          const userPhone = freshUserInfo?.user?.phone_number;
          const isPhoneVerified = freshUserInfo?.verifiedPhone === true;

          // Check feature flag to control phone verification for specific users
          // When flag is true/enabled, phone verification will be shown
          // When flag is false/disabled/undefined, phone verification will be skipped
          const isPhoneVerificationEnabled = await posthog.getFeatureFlag('phone_verification_enabled');
          const shouldPrompt = Boolean(userPhone) && !isPhoneVerified && Boolean(isPhoneVerificationEnabled);

          // Track when verification is skipped due to feature flag
          if (Boolean(userPhone) && !isPhoneVerified && !isPhoneVerificationEnabled) {
            track(AnalyticsEventName.PHONE_VERIFICATION_SKIPPED_BY_FLAG);
          }

          // Only do this once per cart to prevent re-prompting after verification
          if (shouldPrompt && processedCartIdRef.current !== paymentStatus?.cartId) {
            processedCartIdRef.current = paymentStatus?.cartId ?? null;

            // Hide the paid loading overlay so the phone verification drawer is visible
            onPaidLoadingChange(false);

            // Open phone verification prompt with redirect as pending action
            openPhoneVerification({
              phoneNumber: userPhone ?? '',
              orderAnalytics: data.analytics,
              onRedirect: async () => {
                // Invalidate user info to refresh verifiedPhone status
                queryClient.invalidateQueries({ queryKey: [USER_INFO_QUERY_KEY] });
                await redirectHandler();
              },
            });
          } else {
            // Phone already verified or no phone - proceed with redirect
            await redirectHandler();
          }
        }
        return data;
      } catch (error: any) {
        onFormMessageChange({
          commandStatus: 'ERROR',
        });
        if (!error.response) {
          track(AnalyticsEventName.NETWORK_ERROR_TRIGGERED);
          openConnectionAlert();
          return;
        }
        if (
          error?.response?.data?.outcome === 'notReady' &&
          error?.response?.data?.reason === 'paymentError' &&
          error?.response?.data?.error
        ) {
          const titleError = `errors.stripe.${error?.response?.data?.error}.title`;
          const sentenceError = `errors.stripe.${error?.response?.data?.error}.sentence`;
          const genericTitleError = `errors.stripe.generic_decline.title`;
          const genericSentenceError = `errors.stripe.generic_decline.sentence`;
          onErrorChange({
            title: i18n.exists(titleError) ? t(titleError) : t(genericTitleError),
            sentence: i18n.exists(sentenceError) ? t(sentenceError) : t(genericSentenceError),
          });
          if (error?.response?.data?.error === 'payment_intent_authentication_failure') {
            track(AnalyticsEventName.THREE_DS_CHECK_FAILED);
          }
          if (['expired_card', 'incorrect_cvc'].includes(error?.response?.data?.error)) {
            track(AnalyticsEventName.ORDER_CREATION_EXECUTED, {
              output: 'Wrong date/crypto',
            });
          } else if (error?.response?.data?.error === 'payment_intent_authentication_failure') {
            track(AnalyticsEventName.ORDER_CREATION_EXECUTED, {
              output: '3DS_not_completed',
            });
          } else {
            track(AnalyticsEventName.ORDER_CREATION_EXECUTED, {
              output: error?.response?.data?.error,
            });
          }
        }
        if (error?.response?.data?.outcome === 'notReady' && error.response.data.error) {
          track(AnalyticsEventName.ORDER_CREATION_FAILED, {
            error: JSON.stringify(error.response.data.error),
          });
        }
        if (
          error?.response?.data?.outcome === 'notReady' &&
          (error?.response?.data?.reason === 'shippingUnavailable' ||
            error?.response?.data?.reason === 'outOfStock')
        ) {
          queryClient.invalidateQueries({ queryKey: [CART_QUERY_KEY] });
        }
        throw error;
      }
    },
    enabled: Boolean(paymentStatus?.cartId) && authState === 'authenticated' && !paymentStatus?.display,
    retry: (_failCount: number, error: any) => {
      if (!error.response) {
        // case network error
        return false;
      }
      if (_failCount < MAX_FAILURE_COUNT && error?.response?.data?.reason === 'orderNotFound') {
        if (_failCount === 3) {
          toast({
            description: t('warningProcessing'),
            status: 'info',
            position: 'top',
            duration: 5000,
            isClosable: true,
          });
        }
        return true;
      } else if (error?.response?.data?.reason === 'shippingUnavailable') {
        track(AnalyticsEventName.ORDER_ERROR_SHIPPING_UNAVAILABLE, {
          details: JSON.stringify(error?.response?.data?.details),
        });
        onErrorChange({
          title: t('shippingUnavailableError.title'),
          sentence: t('shippingUnavailableError.sentence'),
          reason: 'shippingUnavailable',
          details: error?.response?.data?.details,
        });
        return false;
      } else if (error?.response?.data?.reason === 'outOfStock') {
        track(AnalyticsEventName.ORDER_ERROR_OUT_OF_STOCK, {
          details: JSON.stringify(error?.response?.data?.details),
        });
        onErrorChange({
          title: t('outOfStockError.title'),
          sentence: t('outOfStockError.sentence'),
          reason: 'outOfStock',
          details: error?.response?.data?.details,
        });
        return false;
      } else if (error?.response?.data?.outcome === 'notReady') {
        return false;
      } else {
        toast({
          description: t('failedOrder'),
          status: 'warning',
          position: 'top',
          duration: 10000,
          isClosable: true,
        });
        return false;
      }
    },
  });
};
